import { useCallback, useEffect, useRef, useState } from 'react';
import { api, json } from './api';
import { AllocationPanel } from './AllocationPanel';
import { MarketChart } from './Chart';
import { indicatorRegistry, parseSymbolKey, resolveIndicators } from './indicators';
import type {
  Candle,
  CandleResponse,
  ChartPreferences,
  DrawerKind,
  IdeaLeg,
  IdeaStatus,
  IndicatorParamValue,
  IndicatorSelection,
  Instrument,
  LegSide,
  Session,
  SymbolRequest,
  TradeIdea,
  Watchlist,
} from './types';

const intervals = ['1m', '5m', '15m', '1h', '4h', '1d'];
const defaultPreferences: ChartPreferences = {
  venue: null,
  instrument_id: null,
  interval: '1m',
  indicators: [],
  drawer: null,
  drawer_height: 320,
};

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [loginRequired, setLoginRequired] = useState(false);
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [instrument, setInstrument] = useState<Instrument | null>(null);
  // Transient basket-chart mode: charts an idea's weighted long/short index instead of an instrument.
  const [basket, setBasket] = useState<TradeIdea | null>(null);
  const [candles, setCandles] = useState<CandleResponse | null>(null);
  const [aux, setAux] = useState<Partial<Record<string, Candle[]>>>({});
  const [watchlist, setWatchlist] = useState<Watchlist | null>(null);
  const [status, setStatus] = useState('Initializing research workspace…');
  const requestGeneration = useRef(0);

  const initialize = useCallback(async () => {
    try {
      const nextSession = await api<Session>('/auth/session');
      setSession(nextSession);
      const [{ preferences: saved }, { watchlists }] = await Promise.all([
        api<{ preferences: ChartPreferences }>('/api/preferences/chart'),
        api<{ watchlists: Watchlist[] }>('/api/watchlists'),
      ]);
      const next = { ...defaultPreferences, ...saved };
      setPreferences(next);
      setWatchlist(watchlists[0] ?? null);
      const selected =
        next.venue && next.instrument_id
          ? await api<{ instrument: Instrument }>(
              `/api/instruments/${encodeURIComponent(next.venue)}/${encodeURIComponent(next.instrument_id)}`,
            )
              .then((result) => result.instrument)
              .catch(() => null)
          : (watchlists[0]?.items[0] ?? null);
      setInstrument(selected);
      if (!selected) {
        // Without a selection the candle loader never runs, so this status is what
        // the chart placeholder shows. An empty catalog must not look like a hang.
        const { instruments } = await api<{ instruments: Instrument[] }>('/api/instruments?limit=1');
        setStatus(
          instruments.length
            ? 'Signed in — search for a ticker above to start charting.'
            : 'Signed in, but no instruments are loaded yet. The candle read model is still seeding; search will populate once the projection lands.',
        );
      }
    } catch (error) {
      if (error instanceof Error && 'status' in error && error.status === 401) setLoginRequired(true);
      else setStatus(error instanceof Error ? error.message : 'Unable to initialize dashboard');
    }
  }, []);

  useEffect(() => {
    void initialize();
  }, [initialize]);

  useEffect(() => {
    if ((!instrument && !basket) || !session) return;
    const generation = ++requestGeneration.current;
    const controller = new AbortController();
    setCandles(null);
    setStatus(`Loading ${basket ? basket.title : instrument!.display_symbol}…`);
    const path = basket
      ? `/api/basket-series?${new URLSearchParams({ idea_id: basket.id, interval: preferences.interval, limit: '1500' })}`
      : `/api/candles?${new URLSearchParams({
          venue: instrument!.venue,
          instrument_id: instrument!.instrument_id,
          interval: preferences.interval,
          limit: '1500',
        })}`;
    api<CandleResponse>(path, {}, controller.signal)
      .then((result) => {
        if (generation !== requestGeneration.current) return;
        setCandles(result);
        setStatus(
          result.candles.length
            ? `${result.candles.length.toLocaleString()} bars · ${result.freshness.stale ? 'STALE' : 'current'}`
            : 'No candles available for this interval',
        );
      })
      .catch((error) => {
        if (error?.name !== 'AbortError' && generation === requestGeneration.current) setStatus(error.message);
      });
    return () => controller.abort();
  }, [instrument, basket, preferences.interval, session]);

  // Indicators can request extra candle series (regime symbol, long-SMA filter timeframe).
  useEffect(() => {
    if ((!instrument && !basket) || !session) {
      setAux({});
      return;
    }
    const requests = resolveIndicators(preferences.indicators).flatMap(({ definition, selection }) =>
      (definition.auxSeries?.(selection.params) ?? []).map((request) => ({
        id: `${definition.id}:${request.key}`,
        request,
      })),
    );
    if (!requests.length) {
      setAux({});
      return;
    }
    const controller = new AbortController();
    void Promise.all(
      requests.map(async ({ id, request }) => {
        const interval = request.interval ?? preferences.interval;
        let path: string | null = null;
        if (request.symbol) {
          const target = parseSymbolKey(request.symbol);
          if (target)
            path = `/api/candles?${new URLSearchParams({
              venue: target.venue,
              instrument_id: target.instrumentId,
              interval,
              limit: String(request.limit),
            })}`;
        } else if (basket) {
          // "Chart symbol" aux series in basket mode means the basket index itself.
          path = `/api/basket-series?${new URLSearchParams({ idea_id: basket.id, interval, limit: String(request.limit) })}`;
        } else if (instrument) {
          path = `/api/candles?${new URLSearchParams({
            venue: instrument.venue,
            instrument_id: instrument.instrument_id,
            interval,
            limit: String(request.limit),
          })}`;
        }
        if (!path) return [id, undefined] as const;
        try {
          const result = await api<CandleResponse>(path, {}, controller.signal);
          return [id, result.candles] as const;
        } catch {
          return [id, undefined] as const;
        }
      }),
    ).then((entries) => {
      if (!controller.signal.aborted) setAux(Object.fromEntries(entries));
    });
    return () => controller.abort();
  }, [instrument, basket, preferences.interval, preferences.indicators, session]);

  const savePreferences = async (patch: Partial<ChartPreferences>) => {
    const next = { ...preferences, ...patch };
    setPreferences(next);
    await api('/api/preferences/chart', { method: 'PATCH', body: json(next) });
  };

  const selectInstrument = async (next: Instrument) => {
    setBasket(null);
    setInstrument(next);
    await savePreferences({
      venue: next.venue,
      instrument_id: next.instrument_id,
      interval: next.available_intervals.includes(preferences.interval)
        ? preferences.interval
        : (next.available_intervals[0] ?? '1d'),
    });
  };

  if (loginRequired)
    return (
      <Login
        onSuccess={() => {
          setLoginRequired(false);
          void initialize();
        }}
      />
    );

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark">S</span>
          <strong>Sugon Research</strong>
          <span className="info-chip">INFORMATIONAL ONLY</span>
        </div>
        <SymbolSearch onSelect={selectInstrument} />
        <div className="top-actions">
          <button
            onClick={() => (basket ? setBasket({ ...basket }) : instrument && selectInstrument(instrument))}
            title="Refresh"
          >
            ↻
          </button>
          <span className="identity">{session?.operatorId ?? '…'}</span>
          <button
            onClick={async () => {
              await api('/auth/session', { method: 'DELETE' });
              location.reload();
            }}
          >
            Sign out
          </button>
        </div>
      </header>
      <main className="workspace">
        <WatchlistPanel
          watchlist={watchlist}
          selected={instrument}
          onSelect={selectInstrument}
          onChange={setWatchlist}
        />
        <section className="chart-workspace">
          <div className="chart-toolbar">
            <div className="symbol-heading">
              <strong>{basket ? basket.title : (instrument?.display_symbol ?? 'Select a ticker')}</strong>
              <small>
                {basket
                  ? `basket index (base 100) · ${basket.instruments.length} legs`
                  : instrument
                    ? `${instrument.venue} · ${instrument.asset_class ?? 'market'}`
                    : 'Search or choose a key ticker'}
              </small>
            </div>
            {basket && (
              <button className="basket-exit" onClick={() => setBasket(null)} title="Back to instrument chart">
                ✕ basket
              </button>
            )}
            <div className="intervals">
              {intervals.map((value) => {
                const available = basket ? candles?.instrument.available_intervals : instrument?.available_intervals;
                return (
                  <button
                    key={value}
                    disabled={Boolean(available && !available.includes(value))}
                    className={preferences.interval === value ? 'active' : ''}
                    onClick={() => savePreferences({ interval: value })}
                  >
                    {value}
                  </button>
                );
              })}
            </div>
            <IndicatorMenu
              selections={preferences.indicators}
              onChange={(indicators) => savePreferences({ indicators })}
            />
            <div className={`freshness ${candles?.freshness.stale ? 'stale' : ''}`}>
              <span />
              {candles?.freshness.projected_at
                ? `As of ${new Date(candles.freshness.projected_at).toLocaleString()}`
                : status}
            </div>
          </div>
          <div className="chart-stage">
            {candles ? (
              <MarketChart data={candles} indicators={preferences.indicators} aux={aux} />
            ) : (
              <div className="chart-placeholder">
                <span className="spinner" />
                {status}
              </div>
            )}
          </div>
          <BottomDrawer
            kind={preferences.drawer}
            height={preferences.drawer_height}
            instrument={instrument}
            onChart={setBasket}
            onHeight={(drawer_height) => savePreferences({ drawer_height })}
          />
        </section>
        <nav className="right-rail" aria-label="Research panels">
          <RailButton
            label="Ideas"
            icon="▦"
            active={preferences.drawer === 'ideas'}
            onClick={() => savePreferences({ drawer: preferences.drawer === 'ideas' ? null : 'ideas' })}
          />
          <RailButton
            label="Allocation"
            icon="◫"
            active={preferences.drawer === 'allocation'}
            onClick={() => savePreferences({ drawer: preferences.drawer === 'allocation' ? null : 'allocation' })}
          />
        </nav>
      </main>
      <footer>
        Charts powered by{' '}
        <a href="https://www.tradingview.com/" target="_blank" rel="noreferrer">
          TradingView Lightweight Charts™
        </a>{' '}
        · Research records do not modify execution or risk systems.
      </footer>
    </div>
  );
}

function Login({ onSuccess }: { onSuccess(): void }) {
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  return (
    <div className="login-shell">
      <form
        className="login-card"
        onSubmit={async (event) => {
          event.preventDefault();
          setError('');
          try {
            await api('/auth/session', { method: 'POST', body: json({ token }) });
            onSuccess();
          } catch (caught) {
            setError(caught instanceof Error ? caught.message : 'Sign in failed');
          }
        }}
      >
        <div className="brand">
          <span className="brand-mark">S</span>
          <h1>Sugon Research</h1>
        </div>
        <p>Informational market research, ideas and allocation notes.</p>
        <label>
          Operator bearer token
          <input type="password" value={token} onChange={(event) => setToken(event.target.value)} autoFocus required />
        </label>
        <button className="primary">Sign in</button>
        <span className="error">{error}</span>
      </form>
    </div>
  );
}

function SymbolSearch({ onSelect }: { onSelect(instrument: Instrument): void }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Instrument[]>([]);
  const [open, setOpen] = useState(false);
  const [searched, setSearched] = useState(false);
  const [venues, setVenues] = useState<string[]>([]);
  const [requests, setRequests] = useState<SymbolRequest[]>([]);
  const [requestVenue, setRequestVenue] = useState('');
  const [notice, setNotice] = useState('');
  useEffect(() => {
    setSearched(false);
    if (!query.trim()) {
      setResults([]);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(
      () =>
        api<{ instruments: Instrument[] }>(
          `/api/instruments?q=${encodeURIComponent(query)}&limit=20`,
          {},
          controller.signal,
        )
          .then((body) => {
            setResults(body.instruments);
            setSearched(true);
            setOpen(true);
          })
          .catch(() => undefined),
      180,
    );
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);
  useEffect(() => {
    if (!open) return;
    api<{ venues: string[] }>('/api/venues')
      .then((body) => {
        setVenues(body.venues);
        setRequestVenue((current) => current || body.venues[0] || '');
      })
      .catch(() => undefined);
    api<{ requests: SymbolRequest[] }>('/api/symbol-requests')
      .then((body) => setRequests(body.requests))
      .catch(() => undefined);
  }, [open]);
  const trimmed = query.trim();
  const matchingRequest = requests.find((item) => item.instrument_id.toLowerCase() === trimmed.toLowerCase());
  const submitRequest = async () => {
    setNotice('');
    try {
      const body = await api<{ request: SymbolRequest }>('/api/symbol-requests', {
        method: 'POST',
        body: json({ venue: requestVenue, instrument_id: trimmed }),
      });
      setRequests([body.request, ...requests.filter((item) => item.id !== body.request.id)]);
      setNotice(`Requested ${requestVenue}:${trimmed} — an hourly job loads it from the archive if it exists.`);
    } catch (caught) {
      setNotice(caught instanceof Error ? caught.message : 'Request failed');
    }
  };
  return (
    <div className="symbol-search">
      <span>⌕</span>
      <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onFocus={() => setOpen(true)}
        placeholder="Search ticker, venue, asset…"
      />
      {open && results.length > 0 && (
        <div className="search-results">
          {results.map((item) => (
            <button
              key={`${item.venue}:${item.instrument_id}`}
              onClick={() => {
                onSelect(item);
                setQuery('');
                setOpen(false);
              }}
            >
              <strong>{item.display_symbol}</strong>
              <span>{item.venue}</span>
              <small>{item.asset_class}</small>
            </button>
          ))}
        </div>
      )}
      {open && trimmed && searched && results.length === 0 && (
        <div className="search-results search-empty">
          <p>No catalog match for “{trimmed}”.</p>
          {matchingRequest && (
            <p className={`request-status status-${matchingRequest.status}`}>
              {matchingRequest.venue}:{matchingRequest.instrument_id} — {matchingRequest.status}
              {matchingRequest.detail ? ` · ${matchingRequest.detail}` : ''}
            </p>
          )}
          <div className="request-form">
            <select value={requestVenue} onChange={(event) => setRequestVenue(event.target.value)}>
              {venues.map((venue) => (
                <option key={venue} value={venue}>
                  {venue}
                </option>
              ))}
            </select>
            <button className="primary" onClick={submitRequest} disabled={!requestVenue || !trimmed}>
              Request “{trimmed}”
            </button>
          </div>
          <small>Symbols are venue-native and case-sensitive (e.g. BTC, AAPL, kPEPE).</small>
          {notice && <small className="request-notice">{notice}</small>}
        </div>
      )}
    </div>
  );
}

function WatchlistPanel({
  watchlist,
  selected,
  onSelect,
  onChange,
}: {
  watchlist: Watchlist | null;
  selected: Instrument | null;
  onSelect(i: Instrument): void;
  onChange(w: Watchlist): void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const remove = async (id: string) => {
    if (!watchlist) return;
    await api(`/api/watchlists/${watchlist.id}/items/${id}`, { method: 'DELETE' });
    onChange({ ...watchlist, items: watchlist.items.filter((item) => item.id !== id) });
  };
  const add = async () => {
    if (
      !watchlist ||
      !selected ||
      watchlist.items.some((item) => item.venue === selected.venue && item.instrument_id === selected.instrument_id)
    )
      return;
    const result = await api<{ item: Watchlist['items'][number] }>(`/api/watchlists/${watchlist.id}/items`, {
      method: 'POST',
      body: json({ venue: selected.venue, instrument_id: selected.instrument_id }),
    });
    onChange({ ...watchlist, items: [...watchlist.items, result.item] });
  };
  return (
    <aside className={`watchlist ${collapsed ? 'collapsed' : ''}`}>
      <div className="panel-title">
        <div>
          <strong>{watchlist?.name ?? 'Key Tickers'}</strong>
          <small>{watchlist?.items.length ?? 0} symbols</small>
        </div>
        <button onClick={() => setCollapsed(!collapsed)}>{collapsed ? '›' : '‹'}</button>
      </div>
      {!collapsed && (
        <>
          <button className="add-key" onClick={add}>
            ＋ Add selected ticker
          </button>
          <div className="watch-items">
            {watchlist?.items.map((item) => (
              <div
                className={`watch-row ${selected?.venue === item.venue && selected.instrument_id === item.instrument_id ? 'active' : ''}`}
                key={item.id}
              >
                <button onClick={() => onSelect(item)}>
                  <strong>{item.display_symbol}</strong>
                  <span>{item.venue}</span>
                </button>
                <button className="remove" onClick={() => remove(item.id)}>
                  ×
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </aside>
  );
}

function IndicatorMenu({
  selections,
  onChange,
}: {
  selections: IndicatorSelection[];
  onChange(s: IndicatorSelection[]): void;
}) {
  const [open, setOpen] = useState(false);
  const selectionFor = (id: string) => selections.find((item) => item.id === id);
  const toggle = (id: string) => {
    const definition = indicatorRegistry.find((item) => item.id === id)!;
    const current = selectionFor(id);
    onChange(
      current
        ? selections.map((item) => (item.id === id ? { ...item, enabled: !item.enabled } : item))
        : [...selections, { id, version: definition.version, params: { ...definition.defaults }, enabled: true }],
    );
  };
  const setParam = (id: string, key: string, value: IndicatorParamValue) =>
    onChange(selections.map((item) => (item.id === id ? { ...item, params: { ...item.params, [key]: value } } : item)));
  return (
    <div className="indicator-menu">
      <button onClick={() => setOpen(!open)}>
        ƒ Indicators <span>{selections.filter((item) => item.enabled).length}</span>
      </button>
      {open && (
        <div className="indicator-popover">
          {indicatorRegistry.map((definition) => {
            const selection = selectionFor(definition.id);
            return (
              <div className="indicator-option" key={definition.id}>
                <label>
                  <input type="checkbox" checked={selection?.enabled ?? false} onChange={() => toggle(definition.id)} />
                  <span style={{ color: definition.color }}>●</span>
                  <div>
                    <strong>{definition.label}</strong>
                    <small>{definition.description}</small>
                  </div>
                </label>
                {selection?.enabled && definition.params.length > 0 && (
                  <div className="indicator-params">
                    {definition.params.map((spec) => {
                      const value = selection.params[spec.key] ?? definition.defaults[spec.key];
                      if (spec.kind === 'toggle')
                        return (
                          <label className="param-toggle" key={spec.key}>
                            <input
                              type="checkbox"
                              checked={Boolean(value)}
                              onChange={(event) => setParam(definition.id, spec.key, event.target.checked)}
                            />
                            {spec.label}
                          </label>
                        );
                      if (spec.kind === 'number')
                        return (
                          <label key={spec.key}>
                            {spec.label}
                            <input
                              type="number"
                              step={spec.step ?? 1}
                              value={Number(value)}
                              onChange={(event) => setParam(definition.id, spec.key, Number(event.target.value))}
                            />
                          </label>
                        );
                      if (spec.kind === 'interval')
                        return (
                          <label key={spec.key}>
                            {spec.label}
                            <select
                              value={String(value)}
                              onChange={(event) => setParam(definition.id, spec.key, event.target.value)}
                            >
                              {intervals.map((interval) => (
                                <option key={interval} value={interval}>
                                  {interval}
                                </option>
                              ))}
                            </select>
                          </label>
                        );
                      return (
                        <label key={spec.key}>
                          {spec.label}
                          <input
                            value={String(value)}
                            placeholder="venue:instrument"
                            onChange={(event) => setParam(definition.id, spec.key, event.target.value)}
                          />
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RailButton({
  label,
  icon,
  active,
  onClick,
}: {
  label: string;
  icon: string;
  active: boolean;
  onClick(): void;
}) {
  return (
    <button className={active ? 'active' : ''} onClick={onClick} title={label}>
      <span>{icon}</span>
      <small>{label}</small>
    </button>
  );
}

function BottomDrawer({
  kind,
  height,
  instrument,
  onChart,
  onHeight,
}: {
  kind: DrawerKind;
  height: number;
  instrument: Instrument | null;
  onChart(idea: TradeIdea): void;
  onHeight(h: number): void;
}) {
  const start = useRef<{ y: number; height: number } | null>(null);
  if (!kind) return null;
  return (
    <section className="bottom-drawer" style={{ height }}>
      <div
        className="drawer-resizer"
        onPointerDown={(event) => {
          start.current = { y: event.clientY, height };
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          if (start.current)
            onHeight(Math.max(220, Math.min(620, start.current.height + start.current.y - event.clientY)));
        }}
        onPointerUp={() => {
          start.current = null;
        }}
      />
      <div className="drawer-content">
        {kind === 'ideas' ? <IdeasBoard instrument={instrument} onChart={onChart} /> : <AllocationPanel />}
      </div>
    </section>
  );
}

function IdeasBoard({ instrument, onChart }: { instrument: Instrument | null; onChart(idea: TradeIdea): void }) {
  const [ideas, setIdeas] = useState<TradeIdea[]>([]);
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState('');
  const load = useCallback(() => api<{ ideas: TradeIdea[] }>('/api/ideas').then((body) => setIdeas(body.ideas)), []);
  useEffect(() => {
    void load();
  }, [load]);
  const move = async (id: string, status: IdeaStatus) => {
    await api(`/api/ideas/${id}`, { method: 'PATCH', body: json({ status }) });
    await load();
  };
  const filtered = ideas.filter((idea) =>
    [idea.title, idea.thesis, idea.owner_id, idea.strategy_name, ...idea.tags]
      .join(' ')
      .toLowerCase()
      .includes(filter.toLowerCase()),
  );
  return (
    <div className="ideas-panel">
      <div className="drawer-header">
        <div>
          <h2>Trade Ideas</h2>
          <p>Research lifecycle only — “live” does not start a strategy.</p>
        </div>
        <input placeholder="Filter ideas…" value={filter} onChange={(event) => setFilter(event.target.value)} />
        <button className="primary" onClick={() => setAdding(true)}>
          ＋ New idea
        </button>
      </div>
      {adding && <IdeaForm instrument={instrument} onClose={() => setAdding(false)} onSaved={load} />}
      <div className="kanban">
        {(['idea', 'implementing', 'live'] as IdeaStatus[]).map((column) => (
          <div
            className="kanban-column"
            key={column}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => move(event.dataTransfer.getData('idea-id'), column)}
          >
            <header>
              <strong>{column}</strong>
              <span>{filtered.filter((idea) => idea.status === column).length}</span>
            </header>
            {filtered
              .filter((idea) => idea.status === column)
              .map((idea) => (
                <article
                  draggable
                  onDragStart={(event) => event.dataTransfer.setData('idea-id', idea.id)}
                  key={idea.id}
                >
                  <div className="idea-meta">
                    <span>{idea.strategy_name ?? 'Unassigned'}</span>
                    <span>{idea.owner_id}</span>
                    {idea.instruments.length > 0 && (
                      <button className="chart-idea" title="Chart the basket index" onClick={() => onChart(idea)}>
                        ◫ Chart
                      </button>
                    )}
                  </div>
                  <h3>{idea.title}</h3>
                  <p>{idea.thesis}</p>
                  <div className="tags">
                    {idea.instruments.map((leg) => (
                      <span className={`leg-${leg.side}`} key={`${leg.side}:${leg.venue}:${leg.instrument_id}`}>
                        {leg.side === 'short' ? '−' : '+'}
                        {Number(leg.weight)} {leg.display_symbol}
                      </span>
                    ))}
                    {idea.tags.map((tag) => (
                      <span key={tag}>#{tag}</span>
                    ))}
                  </div>
                </article>
              ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function IdeaForm({
  instrument,
  onClose,
  onSaved,
}: {
  instrument: Instrument | null;
  onClose(): void;
  onSaved(): void;
}) {
  const [title, setTitle] = useState('');
  const [thesis, setThesis] = useState('');
  const [strategy, setStrategy] = useState('');
  const [tags, setTags] = useState('');
  const [legs, setLegs] = useState<IdeaLeg[]>([]);
  const addLeg = (side: LegSide) => {
    if (
      !instrument ||
      legs.some(
        (leg) => leg.side === side && leg.venue === instrument.venue && leg.instrument_id === instrument.instrument_id,
      )
    )
      return;
    setLegs([
      ...legs,
      {
        venue: instrument.venue,
        instrument_id: instrument.instrument_id,
        display_symbol: instrument.display_symbol,
        side,
        weight: '1',
      },
    ]);
  };
  const setWeight = (index: number, weight: string) =>
    setLegs(legs.map((leg, i) => (i === index ? { ...leg, weight } : leg)));
  const removeLeg = (index: number) => setLegs(legs.filter((_, i) => i !== index));
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    await api('/api/ideas', {
      method: 'POST',
      body: json({
        title,
        thesis,
        strategy_name: strategy || null,
        tags: tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
        instruments: legs.map(({ venue, instrument_id, side, weight }) => ({
          venue,
          instrument_id,
          side,
          weight: weight || '1',
        })),
      }),
    });
    onClose();
    onSaved();
  };
  return (
    <form className="inline-form" onSubmit={submit}>
      <input placeholder="Idea title" value={title} onChange={(e) => setTitle(e.target.value)} required />
      <textarea placeholder="Thesis" value={thesis} onChange={(e) => setThesis(e.target.value)} required />
      <input placeholder="Strategy" value={strategy} onChange={(e) => setStrategy(e.target.value)} />
      <input placeholder="tags, comma separated" value={tags} onChange={(e) => setTags(e.target.value)} />
      <button className="primary">Save</button>
      <button type="button" onClick={onClose}>
        Cancel
      </button>
      <div className="idea-legs">
        <button type="button" disabled={!instrument} onClick={() => addLeg('long')}>
          ＋ Long{instrument ? ` ${instrument.display_symbol}` : ''}
        </button>
        <button type="button" disabled={!instrument} onClick={() => addLeg('short')}>
          − Short{instrument ? ` ${instrument.display_symbol}` : ''}
        </button>
        {legs.map((leg, index) => (
          <span className={`idea-leg leg-${leg.side}`} key={`${leg.side}:${leg.venue}:${leg.instrument_id}`}>
            {leg.side === 'short' ? '−' : '+'}
            {leg.display_symbol}
            <input
              type="number"
              min="0"
              step="any"
              value={leg.weight}
              aria-label={`${leg.display_symbol} weight`}
              onChange={(e) => setWeight(index, e.target.value)}
            />
            <button type="button" aria-label={`Remove ${leg.display_symbol}`} onClick={() => removeLeg(index)}>
              ×
            </button>
          </span>
        ))}
        {legs.length === 0 && (
          <small>Long/short basket legs with weights — select a ticker on the chart, then add it as a leg.</small>
        )}
      </div>
    </form>
  );
}
